class DoseHeroCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('.dose-hero-slide'));
    this.dots = Array.from(this.querySelectorAll('.dose-hero-carousel__dot'));
    this.pauseButton = this.querySelector('[data-carousel-pause]');
    this.iconPause = this.querySelector('.dose-hero-carousel__icon-pause');
    this.iconPlay = this.querySelector('.dose-hero-carousel__icon-play');
    this.isAutoplay = this.dataset.autoplay === 'true';
    this.currentIndex = 0;
    this.isPaused = false;
    
    this.init();
  }

  init() {
    // Background videos must not auto-play for reduced-motion users; the
    // fallback image shows instead
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.querySelectorAll('.dose-hero-slide__background video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
        this.showFallbackImage(video);
      });
    } else {
      // Detect when video autoplay is unavailable (e.g. iOS Low Power Mode,
      // data-saver, or browser policy) and fall back to the background image.
      this.setupVideoFallback();
    }

    if (this.slides.length <= 1) return;

    // Attach dot click listeners
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });

    // Attach pause listener
    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', () => this.togglePause());
    }

    // Rely on CSS animation to trigger next slide
    if (this.isAutoplay) {
      this.dots.forEach((dot) => {
        const progress = dot.querySelector('.dose-hero-carousel__dot-progress');
        if (progress) {
          progress.addEventListener('animationend', (e) => {
            // Ensure this is the active dot
            if (dot.classList.contains('is-active')) {
              let nextIndex = this.currentIndex + 1;
              if (nextIndex >= this.slides.length) nextIndex = 0;
              this.goToSlide(nextIndex);
            }
          });
        }
      });
    }
  }

  setupVideoFallback() {
    const videos = this.querySelectorAll('.dose-hero-slide__background video');

    videos.forEach((video) => {
      // If the video already errored out, fall back immediately.
      video.addEventListener('error', () => this.showFallbackImage(video), { once: true });

      const attemptPlay = () => {
        // A muted, inline video should be allowed to autoplay. When autoplay
        // is disabled (iOS Low Power Mode shows a play button instead), the
        // play() promise rejects — that's our signal to show the image.
        const playPromise = video.play();

        if (playPromise !== undefined && typeof playPromise.then === 'function') {
          playPromise
            .then(() => {
              // Autoplay succeeded — ensure the fallback stays hidden.
              this.hideFallbackImage(video);
            })
            .catch(() => {
              this.showFallbackImage(video);
            });
        } else {
          // Older browsers: verify shortly after whether the video advanced.
          window.setTimeout(() => {
            if (video.paused) {
              this.showFallbackImage(video);
            }
          }, 400);
        }
      };

      if (video.readyState >= 2) {
        attemptPlay();
      } else {
        video.addEventListener('loadeddata', attemptPlay, { once: true });
        // Safety net in case loadeddata never fires (e.g. video not loading).
        window.setTimeout(() => {
          if (video.paused) {
            attemptPlay();
          }
        }, 1500);
      }
    });
  }

  // Walk up from the video to its slide, then reveal the fallback image and
  // hide the (non-playing) video for that slide.
  showFallbackImage(video) {
    const slide = video.closest('.dose-hero-slide');
    if (slide) {
      slide.classList.add('dose-hero-slide--video-fallback');
    }
  }

  hideFallbackImage(video) {
    const slide = video.closest('.dose-hero-slide');
    if (slide) {
      slide.classList.remove('dose-hero-slide--video-fallback');
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;
    this.currentIndex = index;
    
    // Move track
    const offset = -100 * this.currentIndex;
    this.track.style.transform = `translateX(${offset}%)`;

    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIndex);
    });
  }

  togglePause() {
    this.isPaused = !this.isPaused;
    this.dataset.paused = this.isPaused;
  }
}

if (!customElements.get('dose-hero-carousel')) {
  customElements.define('dose-hero-carousel', DoseHeroCarousel);
}
